import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash, IconView } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { labelFor, listOptions, useConfig } from '../../context/ConfigContext';

interface CompensationSummary {
  id: number;
  base_amount: string;
  hourly_rate: string | null;
  pay_grade_id: number | null;
  pay_grade_step_id: number | null;
  pay_grade: { id: number; name: string } | null;
  pay_grade_step: { id: number; step_number: number; step_name: string | null } | null;
  change_reason: string | null;
  effective_from: string;
}

interface EmployeePayComponentRow {
  id: number;
  pay_component_id: number;
  pay_component: { id: number; component_name: string; component_type: 'addition' | 'deduction' };
  is_percentage: boolean;
  amount: string;
  effective_from: string;
}

interface BenefitEnrollmentSummary {
  id: number;
  coverage_start: string;
  coverage_end: string | null;
  employee_contribution: string;
  status: 'active' | 'ended';
  plan: { plan_name: string };
  coverage_option: { option_name: string };
}

interface EmployeeRecord {
  id: number;
  full_name: string;
  current_position: {
    position: { pay_grade_id: number; pay_grade: { id: number; name: string } };
    current_compensation: CompensationSummary | null;
    active_pay_components: EmployeePayComponentRow[];
    active_benefit_enrollments: BenefitEnrollmentSummary[];
  } | null;
}

interface BenefitPlanOption {
  id: number;
  plan_name: string;
}

interface BenefitCoverageOptionOption {
  id: number;
  option_name: string;
  employee_cost: string;
  employer_cost: string;
}

interface PayGradeStepOption {
  id: number;
  step_number: number;
  step_name: string | null;
  salary_amount: string;
}

interface PayComponentOption {
  id: number;
  component_name: string;
  component_type: 'addition' | 'deduction';
}

interface PayrollLine {
  id: number;
  type: 'addition' | 'deduction';
  category: string;
  amount: number;
  source: 'manual' | 'component' | 'overtime';
}

interface PayrollEntryRow {
  payroll_entry_id: number;
  period_start: string;
  period_end: string;
  run_status: 'draft' | 'finalized';
  lines: PayrollLine[];
}

const emptyCompensation = {
  pay_grade_step_id: '',
  change_reason: '',
  effective_from: '',
};
const emptyPayComponent = { pay_component_id: '', is_percentage: false, amount: '', effective_from: '' };
const emptyEnrollment = {
  employee_benefit_plan_id: '',
  employee_benefit_coverage_option_id: '',
  coverage_start: '',
  employee_contribution: '',
  employer_contribution: '',
};
const emptyLineForm = { type: 'addition' as 'addition' | 'deduction', category: '', amount: '' };

function formatPeriod(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const s = new Date(start).toLocaleDateString('en-GB', opts);
  const e = new Date(end).toLocaleDateString('en-GB', { ...opts, year: 'numeric' });
  return `${s} – ${e}`;
}

const TABS = ['compensation', 'pay-components', 'benefits', 'additions-deductions'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, string> = {
  compensation: 'Compensation',
  'pay-components': 'Pay Components',
  benefits: 'Benefits',
  'additions-deductions': 'Additions & Deductions',
};

export function EmployeePay() {
  const { id } = useParams<{ id: string }>();
  const confirmDelete = useConfirm();
  const { lists } = useConfig();

  const [tab, setTab] = useState<Tab>('compensation');

  const [record, setRecord] = useState<EmployeeRecord | null>(null);
  const [payrollLines, setPayrollLines] = useState<PayrollEntryRow[] | null>(null);

  const [payGradeSteps, setPayGradeSteps] = useState<PayGradeStepOption[]>([]);
  const [payComponentOptions, setPayComponentOptions] = useState<PayComponentOption[]>([]);
  const [recordingCompensation, setRecordingCompensation] = useState(false);
  const [compensation, setCompensation] = useState(emptyCompensation);
  const [compensationErrors, setCompensationErrors] = useState<Record<string, string[]>>({});
  const [payComponent, setPayComponent] = useState(emptyPayComponent);
  const [payComponentErrors, setPayComponentErrors] = useState<Record<string, string[]>>({});

  const [benefitPlans, setBenefitPlans] = useState<BenefitPlanOption[]>([]);
  const [benefitCoverageOptions, setBenefitCoverageOptions] = useState<BenefitCoverageOptionOption[]>([]);
  const [enrollment, setEnrollment] = useState(emptyEnrollment);
  const [enrollmentErrors, setEnrollmentErrors] = useState<Record<string, string[]>>({});

  const [addingLineFor, setAddingLineFor] = useState<number | null>(null);
  const [lineForm, setLineForm] = useState(emptyLineForm);
  const [editingLine, setEditingLine] = useState<PayrollLine | null>(null);
  const [lineEditForm, setLineEditForm] = useState(emptyLineForm);
  const [lineErrors, setLineErrors] = useState<Record<string, string[]>>({});

  const reload = () => {
    if (!id) return Promise.resolve();
    return Promise.all([
      api.get<EmployeeRecord>(`/employees/${id}`).then((res) => setRecord(res.data)),
      api.get<PayrollEntryRow[]>(`/employees/${id}/payroll-lines`).then((res) => setPayrollLines(res.data)),
    ]);
  };

  useEffect(() => {
    api.get<PayComponentOption[]>('/pay-components').then((res) => setPayComponentOptions(res.data));
    api.get<BenefitPlanOption[]>('/employee-benefit-plans').then((res) => setBenefitPlans(res.data));
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Grade is fixed to the employee's current position, not chosen here —
  // fetch that grade's steps once the position is known.
  const payGradeId = record?.current_position?.position.pay_grade_id;
  useEffect(() => {
    if (!payGradeId) {
      setPayGradeSteps([]);
      return;
    }
    api.get<PayGradeStepOption[]>(`/pay-grades/${payGradeId}/steps`).then((res) => setPayGradeSteps(res.data));
  }, [payGradeId]);

  useEffect(() => {
    if (!enrollment.employee_benefit_plan_id) {
      setBenefitCoverageOptions([]);
      return;
    }
    api
      .get<{ coverage_options: BenefitCoverageOptionOption[] }>(`/employee-benefit-plans/${enrollment.employee_benefit_plan_id}`)
      .then((res) => setBenefitCoverageOptions(res.data.coverage_options));
  }, [enrollment.employee_benefit_plan_id]);

  const recordCompensation = async (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      await api.post(`/employees/${id}/compensation`, {
        ...compensation,
        change_reason: compensation.change_reason || null,
      });
      setCompensation(emptyCompensation);
      setCompensationErrors({});
      setRecordingCompensation(false);
      reload();
    } catch (err) {
      setCompensationErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const addPayComponent = async (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      await api.post(`/employees/${id}/pay-components`, payComponent);
      setPayComponent(emptyPayComponent);
      setPayComponentErrors({});
      reload();
    } catch (err) {
      setPayComponentErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removePayComponent = async (row: EmployeePayComponentRow) => {
    if (!(await confirmDelete(`Remove "${row.pay_component.component_name}"?`))) return;
    await api.delete(`/employee-pay-components/${row.id}`);
    reload();
  };

  const enrollInBenefit = async (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      await api.post(`/employees/${id}/benefit-enrollments`, enrollment);
      setEnrollment(emptyEnrollment);
      setEnrollmentErrors({});
      reload();
    } catch (err) {
      setEnrollmentErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const activityList = (type: 'addition' | 'deduction') =>
    listOptions(lists, type === 'addition' ? 'payroll_addition_type' : 'payroll_deduction_type');

  const categoryLabel = (line: PayrollLine) =>
    labelFor(lists, line.type === 'addition' ? 'payroll_addition_type' : 'payroll_deduction_type', line.category);

  const openAddLine = (entryId: number) => {
    setAddingLineFor(entryId);
    setLineForm(emptyLineForm);
    setLineErrors({});
  };

  const submitAddLine = async (e: FormEvent) => {
    e.preventDefault();
    if (!addingLineFor) return;
    try {
      await api.post(`/payroll-entries/${addingLineFor}/lines`, {
        type: lineForm.type,
        category: lineForm.category,
        amount: Number(lineForm.amount),
      });
      setAddingLineFor(null);
      reload();
    } catch (err) {
      setLineErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const openEditLine = (line: PayrollLine) => {
    setEditingLine(line);
    setLineEditForm({ type: line.type, category: line.category, amount: String(line.amount) });
    setLineErrors({});
  };

  const submitEditLine = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingLine) return;
    try {
      await api.put(`/payroll-entry-lines/${editingLine.id}`, {
        type: lineEditForm.type,
        category: lineEditForm.category,
        amount: Number(lineEditForm.amount),
      });
      setEditingLine(null);
      reload();
    } catch (err) {
      setLineErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removeLine = async (line: PayrollLine) => {
    if (!(await confirmDelete('Delete this line?'))) return;
    await api.delete(`/payroll-entry-lines/${line.id}`);
    reload();
  };

  if (!id || !record) return null;

  const hasPosition = !!record.current_position;

  return (
    <>
      {!hasPosition ? (
        <div className="card">
          <p className="muted">Assign a position to this employee first, then you can manage their pay here.</p>
        </div>
      ) : (
        <>
          <div className="toolbar">
            <div className="tab-group">
              {TABS.map((t) => (
                <button key={t} type="button" className={tab === t ? 'tab active' : 'tab'} onClick={() => setTab(t)}>
                  {TAB_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          {tab === 'compensation' && (
          <div className="card">
            {record.current_position!.current_compensation ? (
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-item-label">Base Amount</span>
                  <span className="detail-item-value">{record.current_position!.current_compensation.base_amount}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-item-label">Hourly Rate</span>
                  <span className="detail-item-value">{record.current_position!.current_compensation.hourly_rate ?? '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-item-label">Pay Grade</span>
                  <span className="detail-item-value">{record.current_position!.current_compensation.pay_grade?.name ?? '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-item-label">Step</span>
                  <span className="detail-item-value">
                    {record.current_position!.current_compensation.pay_grade_step?.step_name ??
                      record.current_position!.current_compensation.pay_grade_step?.step_number ??
                      '—'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-item-label">Effective From</span>
                  <span className="detail-item-value">{record.current_position!.current_compensation.effective_from}</span>
                </div>
              </div>
            ) : (
              <p className="muted">No compensation recorded yet.</p>
            )}
            {!recordingCompensation && (
              <button type="button" className="btn btn-ghost" onClick={() => setRecordingCompensation(true)}>
                Record change
              </button>
            )}
            {recordingCompensation && (
              <form onSubmit={recordCompensation} className="form-grid" style={{ marginTop: 16 }}>
                {compensationErrors.general && <div className="alert alert-error span-2">{compensationErrors.general[0]}</div>}
                <label>
                  Pay Grade
                  <input value={record.current_position!.position.pay_grade.name} disabled />
                </label>
                <label>
                  Step *
                  <select
                    value={compensation.pay_grade_step_id}
                    onChange={(e) => setCompensation({ ...compensation, pay_grade_step_id: e.target.value })}
                    required
                  >
                    <option value="">—</option>
                    {payGradeSteps.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.step_name ?? `Step ${s.step_number}`} — {s.salary_amount}
                      </option>
                    ))}
                  </select>
                  {compensationErrors.pay_grade_step_id && (
                    <small className="error">{compensationErrors.pay_grade_step_id[0]}</small>
                  )}
                </label>
                <p className="muted span-2">
                  Base Amount and Hourly Rate are derived from the chosen step and the position's work shift.
                </p>
                <label>
                  Change Reason
                  <select
                    value={compensation.change_reason}
                    onChange={(e) => setCompensation({ ...compensation, change_reason: e.target.value })}
                  >
                    <option value="">—</option>
                    {listOptions(lists, 'compensation_change_reason').map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {compensationErrors.change_reason && <small className="error">{compensationErrors.change_reason[0]}</small>}
                </label>
                <label>
                  Effective From *
                  <input
                    type="date"
                    value={compensation.effective_from}
                    onChange={(e) => setCompensation({ ...compensation, effective_from: e.target.value })}
                    required
                  />
                  {compensationErrors.effective_from && <small className="error">{compensationErrors.effective_from[0]}</small>}
                </label>
                <div className="form-actions span-2">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setRecordingCompensation(false);
                      setCompensation(emptyCompensation);
                      setCompensationErrors({});
                    }}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-primary">Save</button>
                </div>
              </form>
            )}
          </div>
          )}

          {tab === 'pay-components' && (
          <div className="card">
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Effective From</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {record.current_position!.active_pay_components.map((row) => (
                    <tr key={row.id}>
                      <td>{row.pay_component.component_name}</td>
                      <td>
                        <span className={`badge ${row.pay_component.component_type === 'addition' ? 'badge-active' : 'badge-cancelled'}`}>
                          {row.pay_component.component_type}
                        </span>
                      </td>
                      <td className="muted">{row.is_percentage ? `${row.amount}%` : row.amount}</td>
                      <td className="muted">{row.effective_from}</td>
                      <td className="right">
                        <IconButton icon={IconTrash} label="Remove" variant="danger-ghost" onClick={() => removePayComponent(row)} />
                      </td>
                    </tr>
                  ))}
                  {record.current_position!.active_pay_components.length === 0 && (
                    <tr>
                      <td colSpan={5} className="muted center">
                        No pay components assigned yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <form onSubmit={addPayComponent} className="form-grid" style={{ marginTop: 16 }}>
              {payComponentErrors.general && <div className="alert alert-error span-2">{payComponentErrors.general[0]}</div>}
              <label>
                Component *
                <select
                  value={payComponent.pay_component_id}
                  onChange={(e) => setPayComponent({ ...payComponent, pay_component_id: e.target.value })}
                  required
                >
                  <option value="">—</option>
                  {payComponentOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.component_name} ({c.component_type})
                    </option>
                  ))}
                </select>
                {payComponentErrors.pay_component_id && <small className="error">{payComponentErrors.pay_component_id[0]}</small>}
              </label>
              <label className="checkbox-inline">
                <input
                  type="checkbox"
                  checked={payComponent.is_percentage}
                  onChange={(e) => setPayComponent({ ...payComponent, is_percentage: e.target.checked })}
                />
                Percentage of base salary
              </label>
              <label>
                {payComponent.is_percentage ? 'Percentage *' : 'Amount *'}
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={payComponent.amount}
                  onChange={(e) => setPayComponent({ ...payComponent, amount: e.target.value })}
                  required
                />
                {payComponentErrors.amount && <small className="error">{payComponentErrors.amount[0]}</small>}
              </label>
              <label>
                Effective From *
                <input
                  type="date"
                  value={payComponent.effective_from}
                  onChange={(e) => setPayComponent({ ...payComponent, effective_from: e.target.value })}
                  required
                />
                {payComponentErrors.effective_from && <small className="error">{payComponentErrors.effective_from[0]}</small>}
              </label>
              <div className="form-actions span-2">
                <button className="btn btn-primary">Add pay component</button>
              </div>
            </form>
          </div>
          )}

          {tab === 'benefits' && (
          <div className="card">
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Coverage</th>
                    <th>Employee Contribution</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {record.current_position!.active_benefit_enrollments.map((row) => (
                    <tr key={row.id}>
                      <td>{row.plan.plan_name}</td>
                      <td className="muted">{row.coverage_option.option_name}</td>
                      <td className="muted">{row.employee_contribution}</td>
                      <td>
                        <span className={`badge ${row.status === 'active' ? 'badge-active' : 'badge-cancelled'}`}>{row.status}</span>
                      </td>
                      <td className="right">
                        <IconButton icon={IconView} label="View" to={`/hr/benefit-enrollments/${row.id}`} />
                      </td>
                    </tr>
                  ))}
                  {record.current_position!.active_benefit_enrollments.length === 0 && (
                    <tr>
                      <td colSpan={5} className="muted center">
                        Not enrolled in any benefits yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <form onSubmit={enrollInBenefit} className="form-grid" style={{ marginTop: 16 }}>
              {enrollmentErrors.general && <div className="alert alert-error span-2">{enrollmentErrors.general[0]}</div>}
              <label>
                Plan *
                <select
                  value={enrollment.employee_benefit_plan_id}
                  onChange={(e) =>
                    setEnrollment({ ...enrollment, employee_benefit_plan_id: e.target.value, employee_benefit_coverage_option_id: '' })
                  }
                  required
                >
                  <option value="">—</option>
                  {benefitPlans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.plan_name}
                    </option>
                  ))}
                </select>
                {enrollmentErrors.employee_benefit_plan_id && (
                  <small className="error">{enrollmentErrors.employee_benefit_plan_id[0]}</small>
                )}
              </label>
              <label>
                Coverage Option *
                <select
                  value={enrollment.employee_benefit_coverage_option_id}
                  onChange={(e) => {
                    const option = benefitCoverageOptions.find((o) => String(o.id) === e.target.value);
                    setEnrollment({
                      ...enrollment,
                      employee_benefit_coverage_option_id: e.target.value,
                      employee_contribution: option ? option.employee_cost : enrollment.employee_contribution,
                      employer_contribution: option ? option.employer_cost : enrollment.employer_contribution,
                    });
                  }}
                  disabled={!enrollment.employee_benefit_plan_id}
                  required
                >
                  <option value="">—</option>
                  {benefitCoverageOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.option_name}
                    </option>
                  ))}
                </select>
                {enrollmentErrors.employee_benefit_coverage_option_id && (
                  <small className="error">{enrollmentErrors.employee_benefit_coverage_option_id[0]}</small>
                )}
              </label>
              <label>
                Employee Contribution *
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={enrollment.employee_contribution}
                  onChange={(e) => setEnrollment({ ...enrollment, employee_contribution: e.target.value })}
                  required
                />
                {enrollmentErrors.employee_contribution && <small className="error">{enrollmentErrors.employee_contribution[0]}</small>}
              </label>
              <label>
                Employer Contribution *
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={enrollment.employer_contribution}
                  onChange={(e) => setEnrollment({ ...enrollment, employer_contribution: e.target.value })}
                  required
                />
                {enrollmentErrors.employer_contribution && <small className="error">{enrollmentErrors.employer_contribution[0]}</small>}
              </label>
              <label>
                Coverage Start *
                <input
                  type="date"
                  value={enrollment.coverage_start}
                  onChange={(e) => setEnrollment({ ...enrollment, coverage_start: e.target.value })}
                  required
                />
                {enrollmentErrors.coverage_start && <small className="error">{enrollmentErrors.coverage_start[0]}</small>}
              </label>
              <div className="form-actions span-2">
                <button className="btn btn-primary">Enroll</button>
              </div>
            </form>
          </div>
          )}

          {tab === 'additions-deductions' && (
          <div className="card">
            {payrollLines === null ? (
              <p className="muted">Loading…</p>
            ) : payrollLines.length === 0 ? (
              <p className="muted">No payroll history yet.</p>
            ) : (
              payrollLines.map((entry) => {
                const isDraft = entry.run_status === 'draft';
                return (
                  <div key={entry.payroll_entry_id} style={{ marginBottom: 20 }}>
                    <strong>{formatPeriod(entry.period_start, entry.period_end)}</strong>{' '}
                    <span className={`badge ${isDraft ? 'badge-scheduled' : 'badge-active'}`}>{entry.run_status}</span>
                    <div className="table-scroll" style={{ marginTop: 8 }}>
                      <table>
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Category</th>
                            <th>Amount</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {entry.lines.map((line) => (
                            <tr key={line.id}>
                              <td className="muted">{line.type === 'addition' ? 'Addition' : 'Deduction'}</td>
                              <td>{categoryLabel(line)}</td>
                              <td>{line.amount}</td>
                              <td className="right">
                                {isDraft && (
                                  <>
                                    <IconButton icon={IconEdit} label="Edit line" onClick={() => openEditLine(line)} />
                                    <IconButton
                                      icon={IconTrash}
                                      label="Delete line"
                                      variant="danger-ghost"
                                      onClick={() => removeLine(line)}
                                    />
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                          {entry.lines.length === 0 && (
                            <tr>
                              <td colSpan={4} className="muted center">
                                No additions or deductions.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {isDraft && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ marginTop: 8 }}
                        onClick={() => openAddLine(entry.payroll_entry_id)}
                      >
                        + Add addition / deduction
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
          )}
        </>
      )}

      {addingLineFor && (
        <Modal title="Add addition / deduction" onClose={() => setAddingLineFor(null)}>
          <form onSubmit={submitAddLine} className="form-grid">
            {lineErrors.general && <div className="alert alert-error span-2">{lineErrors.general[0]}</div>}
            <label className="span-2">
              Type *
              <select
                value={lineForm.type}
                onChange={(e) => setLineForm({ ...lineForm, type: e.target.value as 'addition' | 'deduction', category: '' })}
              >
                <option value="addition">Addition</option>
                <option value="deduction">Deduction</option>
              </select>
            </label>
            <label className="span-2">
              Category *
              <select value={lineForm.category} onChange={(e) => setLineForm({ ...lineForm, category: e.target.value })} required>
                <option value="">Select…</option>
                {activityList(lineForm.type).map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {lineErrors.category && <small className="error">{lineErrors.category[0]}</small>}
            </label>
            <label className="span-2">
              Amount *
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={lineForm.amount}
                onChange={(e) => setLineForm({ ...lineForm, amount: e.target.value })}
                required
              />
              {lineErrors.amount && <small className="error">{lineErrors.amount[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setAddingLineFor(null)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={!lineForm.category || !lineForm.amount}>
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editingLine && (
        <Modal title="Edit line" onClose={() => setEditingLine(null)}>
          <form onSubmit={submitEditLine} className="form-grid">
            {lineErrors.general && <div className="alert alert-error span-2">{lineErrors.general[0]}</div>}
            <label className="span-2">
              Type *
              <select
                value={lineEditForm.type}
                onChange={(e) =>
                  setLineEditForm({ ...lineEditForm, type: e.target.value as 'addition' | 'deduction', category: '' })
                }
              >
                <option value="addition">Addition</option>
                <option value="deduction">Deduction</option>
              </select>
            </label>
            <label className="span-2">
              Category *
              <select
                value={lineEditForm.category}
                onChange={(e) => setLineEditForm({ ...lineEditForm, category: e.target.value })}
                required
              >
                {activityList(lineEditForm.type).map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {lineErrors.category && <small className="error">{lineErrors.category[0]}</small>}
            </label>
            <label className="span-2">
              Amount *
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={lineEditForm.amount}
                onChange={(e) => setLineEditForm({ ...lineEditForm, amount: e.target.value })}
                required
              />
              {lineErrors.amount && <small className="error">{lineErrors.amount[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditingLine(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
